import React, { useState, useEffect } from 'react';
import {
   Box,
   Card,
   CardContent,
   Typography,
   Grid,
   Divider,
   CircularProgress,
   Alert,
   Chip,
   useTheme,
   useMediaQuery,
   Avatar,
   List,
   ListItem,
   ListItemAvatar,
   ListItemText,
} from '@mui/material';
import { getAuthHeaders } from '../../../utils/auth';
import { format } from 'date-fns';
import {
   AreaChart,
   Area,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   ResponsiveContainer,
   LineChart,
   Line,
   Legend,
} from 'recharts';
import {
   Visibility as VisibilityIcon,
   QrCode as QrCodeIcon,
   PersonAdd as PersonAddIcon,
} from '@mui/icons-material';

interface EventAnalyticsStats {
   totalViews: number;
   uniqueViews: number;
   totalScans: number;
   uniqueScans: number;
   totalSignups: number;
   lastView: string | null;
   viewsByDate: {
      [date: string]: {
         total: number;
         unique: number;
         shareable_link: number;
         qr_scan: number;
      };
   };
   viewsBySource: {
      shareable_link: number;
      qr_scan: number;
   };
   uniqueViewsBySource: {
      shareable_link: number;
      qr_scan: number;
   };
   recentViews: ViewRecord[];
   recentSignups: SignupRecord[];
}

interface ViewRecord {
   id: string;
   fingerprint: string;
   viewerId: string | null;
   viewerName: string;
   viewerEmail: string | null;
   viewerPhotoURL: string | null;
   viewedAt: string;
   isUnique: boolean;
   signedUp: boolean;
   source: 'shareable_link' | 'qr_scan';
}

interface SignupRecord {
   id: string;
   userId: string;
   userName: string;
   userEmail: string | null;
   userPhotoURL: string | null;
   signedUpAt: string;
}

interface EventAnalyticsProps {
   eventId: string;
}

const EventAnalytics: React.FC<EventAnalyticsProps> = ({ eventId }) => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [stats, setStats] = useState<EventAnalyticsStats | null>(null);

   useEffect(() => {
      fetchEventAnalytics();
   }, [eventId]);

   const fetchEventAnalytics = async () => {
      try {
         setLoading(true);
         setError(null);

         const headers = await getAuthHeaders();
         const response = await fetch(`/api/events/${eventId}/analytics`, {
            headers,
         });

         if (!response.ok) {
            throw new Error('Failed to fetch event analytics');
         }

         const data = await response.json();
         setStats(data);
      } catch (err) {
         console.error('Error fetching event analytics:', err);
         setError('Failed to load event analytics');
      } finally {
         setLoading(false);
      }
   };

   const getChartData = () => {
      if (!stats || !stats.viewsByDate) return [];

      // Convert viewsByDate to array
      const chartData = Object.entries(stats.viewsByDate)
         .map(([date, data]) => ({
            date,
            'Total Views': data.total || 0,
            'Unique Views': data.unique || 0,
            'QR Scans': data.qr_scan || 0,
            dateFormatted: format(new Date(date), 'MMM dd'),
         }))
         .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Get last 30 days
      return chartData.slice(-30);
   };

   const getScanChartData = () => {
      if (!stats || !stats.viewsByDate) return [];

      // Convert viewsByDate to array and extract scan data
      const chartData = Object.entries(stats.viewsByDate)
         .map(([date, data]) => ({
            date,
            scans: data.qr_scan || 0,
            dateFormatted: format(new Date(date), 'MMM dd'),
         }))
         .filter((item) => item.scans > 0) // Only include dates with scans
         .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Get last 30 days
      return chartData.slice(-30);
   };

   if (loading) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
         </Box>
      );
   }

   if (error) {
      return (
         <Alert severity='error' sx={{ mb: 3 }}>
            {error}
         </Alert>
      );
   }

   if (!stats) {
      return (
         <Alert severity='info' sx={{ mb: 3 }}>
            No analytics data available
         </Alert>
      );
   }

   const chartData = getChartData();
   const scanChartData = getScanChartData();

   return (
      <Box>
         {/* Overview Stats */}
         <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
               <Card sx={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)' }}>
                  <CardContent>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <VisibilityIcon sx={{ color: 'white', mr: 1 }} />
                        <Typography variant='h6' sx={{ color: 'white', fontWeight: 600 }}>
                           Total Views
                        </Typography>
                     </Box>
                     <Typography variant='h3' sx={{ color: 'white', fontWeight: 'bold' }}>
                        {stats.totalViews}
                     </Typography>
                     <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.9)', mt: 1 }}>
                        Unique: {stats.uniqueViews}
                     </Typography>
                  </CardContent>
               </Card>
            </Grid>
            <Grid item xs={12} md={4}>
               <Card sx={{ background: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)' }}>
                  <CardContent>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <QrCodeIcon sx={{ color: 'white', mr: 1 }} />
                        <Typography variant='h6' sx={{ color: 'white', fontWeight: 600 }}>
                           QR Scans
                        </Typography>
                     </Box>
                     <Typography variant='h3' sx={{ color: 'white', fontWeight: 'bold' }}>
                        {stats.totalScans}
                     </Typography>
                     <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.9)', mt: 1 }}>
                        Unique: {stats.uniqueScans}
                     </Typography>
                  </CardContent>
               </Card>
            </Grid>
            <Grid item xs={12} md={4}>
               <Card sx={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}>
                  <CardContent>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonAddIcon sx={{ color: 'white', mr: 1 }} />
                        <Typography variant='h6' sx={{ color: 'white', fontWeight: 600 }}>
                           Signups
                        </Typography>
                     </Box>
                     <Typography variant='h3' sx={{ color: 'white', fontWeight: 'bold' }}>
                        {stats.totalSignups}
                     </Typography>
                     <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.9)', mt: 1 }}>
                        Through event page
                     </Typography>
                  </CardContent>
               </Card>
            </Grid>
         </Grid>

         {/* QR Scans Chart */}
         {scanChartData.length > 0 && (
            <Card sx={{ mb: 4 }}>
               <CardContent>
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                     }}
                  >
                     <Typography variant='h6' sx={{ fontWeight: 600 }}>
                        📱 QR Code Scans
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        Total: {stats.totalScans} | Unique: {stats.uniqueScans}
                     </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <ResponsiveContainer width='100%' height={200}>
                     <AreaChart data={scanChartData}>
                        <defs>
                           <linearGradient id='colorScans' x1='0' y1='0' x2='0' y2='1'>
                              <stop offset='5%' stopColor='#06B6D4' stopOpacity={0.8} />
                              <stop offset='95%' stopColor='#06B6D4' stopOpacity={0.1} />
                           </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                        <XAxis
                           dataKey='dateFormatted'
                           stroke='#6b7280'
                           style={{ fontSize: isMobile ? '10px' : '12px' }}
                        />
                        <YAxis stroke='#6b7280' style={{ fontSize: isMobile ? '10px' : '12px' }} />
                        <Tooltip
                           contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                           }}
                        />
                        <Area
                           type='monotone'
                           dataKey='scans'
                           stroke='#06B6D4'
                           fillOpacity={1}
                           fill='url(#colorScans)'
                           name='Scans'
                        />
                     </AreaChart>
                  </ResponsiveContainer>
               </CardContent>
            </Card>
         )}

         {/* Source Breakdown */}
         <Card sx={{ mb: 4 }}>
            <CardContent>
               <Typography variant='h6' sx={{ fontWeight: 600, mb: 3 }}>
                  🎯 Traffic Sources
               </Typography>
               <Divider sx={{ mb: 3 }} />
               <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                     <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f3f4f6', borderRadius: 2 }}>
                        <Typography variant='body2' color='text.secondary' gutterBottom>
                           Shareable Link
                        </Typography>
                        <Typography variant='h4' sx={{ fontWeight: 'bold', color: '#4F46E5' }}>
                           {stats.viewsBySource?.shareable_link || 0}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                           Unique: {stats.uniqueViewsBySource?.shareable_link || 0}
                        </Typography>
                     </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                     <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f3f4f6', borderRadius: 2 }}>
                        <Typography variant='body2' color='text.secondary' gutterBottom>
                           QR Code Scan
                        </Typography>
                        <Typography variant='h4' sx={{ fontWeight: 'bold', color: '#06B6D4' }}>
                           {stats.viewsBySource?.qr_scan || 0}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                           Unique: {stats.uniqueViewsBySource?.qr_scan || 0}
                        </Typography>
                     </Box>
                  </Grid>
               </Grid>
            </CardContent>
         </Card>

         {/* Recent Signups */}
         {stats.recentSignups && stats.recentSignups.length > 0 && (
            <Card sx={{ mb: 4 }}>
               <CardContent>
                  <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
                     👤 Recent Signups
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List>
                     {stats.recentSignups.map((signup) => (
                        <ListItem key={signup.id}>
                           <ListItemAvatar>
                              <Avatar src={signup.userPhotoURL || undefined} alt={signup.userName}>
                                 {signup.userName.charAt(0).toUpperCase()}
                              </Avatar>
                           </ListItemAvatar>
                           <ListItemText
                              primary={signup.userName}
                              secondary={`${signup.userEmail || 'No email'} • ${format(
                                 new Date(signup.signedUpAt),
                                 'MMM dd, yyyy h:mm a'
                              )}`}
                           />
                        </ListItem>
                     ))}
                  </List>
               </CardContent>
            </Card>
         )}
      </Box>
   );
};

export default EventAnalytics;
